import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "./Forex.css";
import PairCard from "./PairCard";
import websocket from "../services/websocket";
import ForexHeader from "./ForexHeader";
import ForexBottomNav from "../Components/ForexBottomNav";
// ==========================================
// FOREX
// ==========================================

const Forex = () => {
  const [pairs, setPairs] = useState([]);
  const [charts, setCharts] = useState({});
  const [search, setSearch] = useState("");

  // ALL = onyesha pairs zote
  const [activeCurrency, setActiveCurrency] =
    useState("ALL");

  const [loading, setLoading] = useState(true);
  const [marketLocked, setMarketLocked] = useState(() => {
    const day = new Date().getDay();
    return day === 0 || day === 6;
  });
  
  useEffect(() => {
    const checkMarketDay = () => {
      const day = new Date().getDay();
      setMarketLocked(day === 0 || day === 6);
    };
  
    checkMarketDay();
  
    const timer = setInterval(checkMarketDay, 60 * 1000);
  
    return () => clearInterval(timer);
  }, []);
  // ==========================================
  // CURRENCIES
  // ==========================================

  const currencies = [
    { code: "ALL", flag: "🌍" },
    { code: "USD", flag: "🇺🇸" },                                  
    { code: "GBP", flag: "🇬🇧" },
    { code: "EUR", flag: "🇪🇺" },
    { code: "JPY", flag: "🇯🇵" },
    { code: "CAD", flag: "🇨🇦" },
    { code: "AUD", flag: "🇦🇺" },
    { code: "SEK", flag: "🇸🇪" },
    { code: "CHF", flag: "🇨🇭" },
    { code: "NZD", flag: "🇳🇿" },
  ];

  // ==========================================
  // CLEAN SYMBOL
  // ==========================================

  const cleanForexSymbol = (apiSymbol) => {
    return String(apiSymbol || "")
      .replace(/^frx/i, "")
      .replace(/[^a-z]/gi, "")
      .toUpperCase();
  };

  // ==========================================
  // GET API SYMBOL
  // ==========================================

  const getApiSymbol = (item) => {
    return String(
      item?.underlying_symbol ||
        item?.symbol ||
        ""
    ).trim();
  };



// ==========================================
// ALWAYS OPEN FOREX FROM TOP
// ==========================================

useEffect(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant",
  });

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}, []);

  // ==========================================
  // WEBSOCKET
  // ==========================================

  useEffect(() => {
    websocket.connect();

    const unsubscribe = websocket.onTick(
      (data) => {
        // ======================================
        // DEBUG API ERRORS
        // ======================================

        if (data?.error) {
          console.error(
            "❌ DERIV API ERROR:",
            data.error
          );

          return;
        }

        // ======================================
        // 1. ACTIVE SYMBOLS
        // ======================================

        if (
          data.msg_type === "active_symbols" ||
          Array.isArray(data.active_symbols)
        ) {
          const allSymbols = Array.isArray(
            data.active_symbols
          )
            ? data.active_symbols
            : [];

          console.log(
            "🔥 ALL ACTIVE SYMBOLS:",
            allSymbols
          );

          console.log(
            "🔥 TOTAL ACTIVE SYMBOLS:",
            allSymbols.length
          );

          // ====================================
          // FIND FOREX SYMBOLS
          // ====================================

          const forexSymbols =
            allSymbols.filter((item) => {
              const apiSymbol =
                getApiSymbol(item);

              const market = String(
                item?.market || ""
              ).toLowerCase();

              const submarket = String(
                item?.submarket || ""
              ).toLowerCase();

              const marketDisplayName =
                String(
                  item?.market_display_name ||
                    ""
                ).toLowerCase();

              const submarketDisplayName =
                String(
                  item?.submarket_display_name ||
                    ""
                ).toLowerCase();

              const symbolLower =
                apiSymbol.toLowerCase();

              return (
                symbolLower.startsWith(
                  "frx"
                ) ||
                market === "forex" ||
                market.includes("forex") ||
                submarket.includes(
                  "forex"
                ) ||
                marketDisplayName.includes(
                  "forex"
                ) ||
                submarketDisplayName.includes(
                  "forex"
                )
              );
            });

          console.log(
            "💱 RAW FOREX SYMBOLS:",
            forexSymbols
          );

          console.log(
            "💱 RAW FOREX COUNT:",
            forexSymbols.length
          );

          // ====================================
          // MAP FOREX PAIRS
          // ====================================

          const mappedPairs =
            forexSymbols
              .map((item) => {
                const apiSymbol =
                  getApiSymbol(item);

                if (!apiSymbol) {
                  return null;
                }

                const cleanSymbol =
                  cleanForexSymbol(
                    apiSymbol
                  );

                if (!cleanSymbol) {
                  return null;
                }

                return {
                  pair: cleanSymbol,

                  apiSymbol,

                  name:
                    item
                      ?.underlying_symbol_name ||
                    item?.display_name ||
                    cleanSymbol,

                  bid: "--",

                  ask: "--",

                  spread: "--",

                  trend: "up",
                };
              })
              .filter(Boolean);

          // ====================================
          // REMOVE DUPLICATES
          // ====================================

          const uniqueMap = new Map();

          mappedPairs.forEach((item) => {
            if (
              !uniqueMap.has(
                item.apiSymbol
              )
            ) {
              uniqueMap.set(
                item.apiSymbol,
                item
              );
            }
          });

          const uniquePairs =
            Array.from(
              uniqueMap.values()
            );

          // ====================================
          // SORT
          // ====================================

          uniquePairs.sort((a, b) =>
            a.pair.localeCompare(b.pair)
          );

          console.log(
            "✅ FINAL FOREX COUNT:",
            uniquePairs.length
          );

          console.table(
            uniquePairs.map((item) => ({
              pair: item.pair,
              apiSymbol:
                item.apiSymbol,
              name: item.name,
            }))
          );

          // ====================================
          // SAVE
          // ====================================

          setPairs(uniquePairs);

          setLoading(false);

          // ====================================
          // REQUEST HISTORY + LIVE TICKS
          // ====================================

          uniquePairs.forEach(
            (item) => {
              websocket.history(
                item.apiSymbol,
                30
              );
            }
          );

          return;
        }

        // ======================================
        // 2. HISTORY
        // ======================================

        if (data.history) {
          const apiSymbol = String(
            data.echo_req
              ?.ticks_history || ""
          );

          if (!apiSymbol) {
            return;
          }

          const symbol =
            cleanForexSymbol(
              apiSymbol
            );

          if (!symbol) {
            return;
          }

          const prices = Array.isArray(
            data.history.prices
          )
            ? data.history.prices
                .map(Number)
                .filter(
                  Number.isFinite
                )
            : [];

          if (!prices.length) {
            return;
          }

          // ====================================
          // CHART
          // ====================================

          setCharts((old) => ({
            ...old,

            [symbol]:
              prices.slice(-30),
          }));

          // ====================================
          // LAST PRICE
          // ====================================

          const bid =
            prices[
              prices.length - 1
            ];

          const previous =
            prices.length >= 2
              ? prices[
                  prices.length - 2
                ]
              : bid;

          // Temporary synthetic ask.
          // Hii si broker bid/ask halisi.
          const ask =
            bid + 0.0003;

          // ====================================
          // UPDATE PAIR
          // ====================================

          setPairs((old) =>
            old.map((item) => {
              if (
                item.apiSymbol !==
                  apiSymbol &&
                item.pair !== symbol
              ) {
                return item;
              }

              return {
                ...item,

                bid:
                  bid.toFixed(5),

                ask:
                  ask.toFixed(5),

                spread:
                  Math.round(
                    Math.abs(
                      ask - bid
                    ) * 100000
                  ),

                trend:
                  bid >= previous
                    ? "up"
                    : "down",
              };
            })
          );

          return;
        }

        // ======================================
        // 3. LIVE TICK
        // ======================================

        if (data.tick) {
          const apiSymbol = String(
            data.tick.symbol ||
              data.tick
                .underlying_symbol ||
              ""
          );

          if (!apiSymbol) {
            return;
          }

          const symbol =
            cleanForexSymbol(
              apiSymbol
            );

          if (!symbol) {
            return;
          }

          const price = Number(
            data.tick.quote
          );

          if (
            !Number.isFinite(price)
          ) {
            return;
          }

          // ====================================
          // UPDATE CHART
          // ====================================

          setCharts((old) => {
            const oldPrices =
              Array.isArray(
                old[symbol]
              )
                ? old[symbol]
                : [];

            const list = [
              ...oldPrices,
              price,
            ];

            if (
              list.length > 30
            ) {
              list.splice(
                0,
                list.length - 30
              );
            }

            return {
              ...old,

              [symbol]: list,
            };
          });

          // ====================================
          // UPDATE PRICE
          // ====================================

          setPairs((old) =>
            old.map((item) => {
              if (
                item.apiSymbol !==
                  apiSymbol &&
                item.pair !== symbol
              ) {
                return item;
              }

              const previous =
                Number(item.bid);

              const ask =
                price + 0.0003;

              return {
                ...item,

                bid:
                  price.toFixed(5),

                ask:
                  ask.toFixed(5),

                spread:
                  Math.round(
                    Math.abs(
                      ask - price
                    ) * 100000
                  ),

                trend:
                  Number.isFinite(
                    previous
                  ) &&
                  price < previous
                    ? "down"
                    : "up",
              };
            })
          );

          return;
        }
      }
    );

    // ========================================
    // CLEANUP
    // ========================================

    return () => {
      unsubscribe();

      websocket.close();
    };
  }, []);

  // ==========================================
  // FILTER PAIRS
  // ==========================================

  const filteredPairs =
    useMemo(() => {
      let list = [...pairs];

      // ======================================
      // CURRENCY FILTER
      // ======================================

      if (
        activeCurrency !== "ALL"
      ) {
        list = list.filter(
          (item) => {
            const pair = String(
              item.pair || ""
            ).toUpperCase();

            return (
              pair.startsWith(
                activeCurrency
              ) ||
              pair.endsWith(
                activeCurrency
              )
            );
          }
        );
      }

      // ======================================
      // SEARCH
      // ======================================

      if (search.trim()) {
        const query = search
          .trim()
          .toUpperCase();

        list = list.filter(
          (item) => {
            const pair = String(
              item.pair || ""
            ).toUpperCase();

            const name = String(
              item.name || ""
            ).toUpperCase();

            return (
              pair.includes(query) ||
              name.includes(query)
            );
          }
        );
      }

      return list;
    }, [
      pairs,
      search,
      activeCurrency,
    ]);

  // ==========================================
  // UI
  // ==========================================

 
      {/* =====================================
          TOP AREA
      ====================================== */}


      return (
        <div className="forex-container">
      
          {/* HEADER YAKE BINAFSI */}
          <ForexHeader />
          <ForexBottomNav />

          {/* SEARCH + CURRENCIES */}
          <div className="forex-top-area">
      
            {/* SEARCH */}
            <div className="forex-search-row">
      
              <div className="forex-search-box">
                <span className="forex-search-icon">
                  ⌕
                </span>
      
                <input
                  type="text"
                  placeholder="Search All Symbols"
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                />
              </div>
      
              <button
                type="button"
                className="forex-settings-btn"
              >
                ⚙️
              </button>
      
            </div>
      
            {/* CURRENCIES */}
            <div className="forex-currencies">
      
              {currencies.map((item) => (
                <button
                  type="button"
                  key={item.code}
                  className={
                    activeCurrency === item.code
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setActiveCurrency(item.code)
                  }
                >
                  <span>{item.flag}</span>
                  <strong>{item.code}</strong>
                </button>
              ))}
      
            </div>
      
          </div>
      
          {/* COUNT */}
          {!loading && (
            <div
              style={{
                padding: "5px 10px",
                fontSize: "11px",
                color: "#888",
              }}
            >
              Showing {filteredPairs.length} of{" "}
              {pairs.length} Forex pairs
            </div>
          )}
      
          {/* LOADING */}
          {loading && (
            <div className="forex-loading">
              Loading Forex...
            </div>
          )}
      
          {/* CARDS */}
          {!loading && (
            <div className="forex-cards">
      
              {filteredPairs.length > 0 ? (
                filteredPairs.map((pair) => (
                  <PairCard
                  key={pair.apiSymbol}
                  pair={pair}
                  chart={charts[pair.pair] || []}
                  marketLocked={marketLocked}
                />
                ))
              ) : (
                <div className="forex-no-pairs">
      
                  No Forex pairs found
      
                  {activeCurrency !== "ALL" && (
                    <button
                      type="button"
                      onClick={() =>
                        setActiveCurrency("ALL")
                      }
                      style={{
                        display: "block",
                        margin: "12px auto 0",
                      }}
                    >
                      Show All
                    </button>
                  )}
      
                </div>
              )}
      
            </div>
          )}
      
        </div>
      );
};

export default Forex;





